export const FAV_PLAN_BASE_PATH = '/fav-plan';

/**
 * Pasos del formulario de My Favorite Plan.
 *
 * `slug` es la parte estable de la URL; `path` es la ruta del modo "crear" y se
 * deriva de él. El modo "editar" antepone el id (`/fav-plan/:id/<slug>`), así que
 * ningún consumidor debería comparar contra `path` directamente: para ubicar el
 * paso actual está `findFavPlanStepIndex` y para navegar `buildFavPlanPath`.
 *
 * Varios pasos comparten `id` (los riders) porque el sidebar los agrupa en un
 * único módulo visual.
 *
 * `label` es solo el fallback si `texts[labelKey]` no está disponible (ver
 * FavoritePlanSidebarSteps) - el sidebar no debe imprimir `label` directo.
 *
 * `labelKey` reusa las keys del CMS que ya usa el sidebar del cotizador para
 * estos mismos steps (ver components/StepComponents.jsx) en vez de duplicar
 * traducciones propias: son el mismo concepto de paso y ya están traducidas
 * ahí en los 3 idiomas. `base-information` es la excepción - el cotizador
 * llama a su primer step "Personal Information" (recolecta más campos), así
 * que reusa `favorite_plan_form_base_info_title` (el título de esta misma
 * página) en vez de la key del cotizador.
 */
const STEPS = [
    { id: 1, label: 'Base Information',     labelKey: 'favorite_plan_form_base_info_title',       slug: 'base-information' },
    { id: 2, label: 'Coverage Options',     labelKey: 'menu_options_title_coverage',              slug: 'coverage-options' },
    { id: 3, label: 'Plan Benefits',        labelKey: 'menu_options_title_benefits',              slug: 'plan-benefits' },
    { id: 4, label: 'Outpatient Benefits',  labelKey: 'menu_options_title_benefits_ambulatorios', slug: 'outpatient-benefits' },
    { id: 5, label: 'Deductible Reduction', labelKey: 'menu_options_title_reduccion_deducible',   slug: 'deductible-reduction' },
    { id: 6, label: 'Riders',               labelKey: 'menu_options_title_riders',                slug: 'riders-maternity' },
    { id: 6, label: 'Riders',               labelKey: 'menu_options_title_riders',                slug: 'riders-preventive-checkup' },
    { id: 6, label: 'Riders',               labelKey: 'menu_options_title_riders',                slug: 'riders-eye-surgery' },
    { id: 7, label: 'Summary',              labelKey: 'menu_options_title_summary',               slug: 'summary' },
];

export const MY_FAVORITE_PLAN_STEPS = STEPS.map(step => ({
    ...step,
    path: `${FAV_PLAN_BASE_PATH}/${step.slug}`,
}));

export const FAV_PLAN_FIRST_STEP_SLUG = MY_FAVORITE_PLAN_STEPS[0].slug;
export const FAV_PLAN_SUMMARY_SLUG = MY_FAVORITE_PLAN_STEPS[MY_FAVORITE_PLAN_STEPS.length - 1].slug;

/**
 * Construye la ruta de un paso. Con `id` devuelve la del modo edición.
 *
 * @param {string} slug
 * @param {string} [id]  id del plan favorito que se está editando
 */
export const buildFavPlanPath = (slug, id) => (id
    ? `${FAV_PLAN_BASE_PATH}/${id}/${slug}`
    : `${FAV_PLAN_BASE_PATH}/${slug}`);

/**
 * Índice del paso actual dentro de `MY_FAVORITE_PLAN_STEPS`, sirva o no la URL
 * un `:id` (`/fav-plan/summary` y `/fav-plan/<id>/summary` resuelven igual).
 * Devuelve -1 si el pathname no corresponde a ningún paso.
 */
export const findFavPlanStepIndex = (pathname) => MY_FAVORITE_PLAN_STEPS
    .findIndex(step => pathname.endsWith(`/${step.slug}`));

/**
 * Pasos realmente navegables dado el estado actual del formulario: el rider de
 * maternidad se omite si el usuario no es elegible. Mismo criterio que usa el
 * cotizador para excluir su propio step de maternidad de `getSteps`
 * (ver `ignore: !isFemale()` en hooks/useSteps.jsx) - acá se aplica sobre esta
 * lista en vez de generarla dinámicamente porque el sidebar necesita además la
 * lista completa (para las etiquetas de los módulos).
 */
export const getApplicableFavPlanSteps = (isEligibleForMaternity) => MY_FAVORITE_PLAN_STEPS
    .filter(step => step.slug !== 'riders-maternity' || isEligibleForMaternity);
